import { Modal, ModalBody, ModalHeader } from 'reactstrap'
import { FC, ReactNode, useCallback } from 'react'

export interface ModalDefaultProps {
  title?: ReactNode
  isOpen: boolean
  sideModal?: boolean
  modalClassName?: string
  className?: string
  /** Fired *after* the modal actually closes */
  onDismiss: () => void
  /** Toggles the open state; usually just flips `isOpen` */
  toggle: () => void
  /**
   * Intercept any attempt to close (header X, backdrop click, ESC).
   * Return `true` to allow closing, `false` to prevent.
   */
  onBeforeClose?: () => Promise<boolean>
}

const ModalDefault: FC<ModalDefaultProps> = ({
  children,
  className,
  isOpen,
  modalClassName,
  onBeforeClose,
  onDismiss,
  sideModal,
  title,
  toggle,
}) => {
  const handleToggle = useCallback(async () => {
    // if the user provided an interceptor, run it first
    if (onBeforeClose) {
      let shouldClose = false
      try {
        shouldClose = await onBeforeClose()
      } catch (err) {
        // swallow errors, but don’t close
        console.error('onBeforeClose threw:', err)
        shouldClose = false
      }
      if (!shouldClose) return
    }

    // proceed with dismissal
    onDismiss()
    toggle()
  }, [onBeforeClose, onDismiss, toggle])

  return (
    <Modal
      isOpen={isOpen}
      unmountOnClose
      modalClassName={sideModal ? 'side-modal' : modalClassName}
      toggle={handleToggle}
    >
      {!!title && <ModalHeader toggle={handleToggle}>{title}</ModalHeader>}
      <ModalBody className={className}>{children}</ModalBody>
    </Modal>
  )
}

export default ModalDefault
